// A06: sandbox file tree rendered in the left rail under each
// connected repo. Lazy-expand folders; clicking a file opens it as a
// tab in the FilesTab view. react-arborist handles the tree geometry
// (virtualised, keyboard nav).

import { useCallback, useEffect, useMemo, useState } from "react";
import { Tree, type NodeApi } from "react-arborist";
import { ChevronDown, ChevronRight, File as FileIcon } from "lucide-react";
import {
  ApiError,
  readWorkspaceFile,
  type SandboxDirEntry,
} from "../api/client";

interface Props {
  workspaceId: number;
  repo: string;
  onOpenFile: (path: string) => void;
}

interface Node {
  id: string;
  name: string;
  kind: "file" | "directory";
  path: string;
  children?: Node[];
  loaded: boolean;
}

function buildRoot(repo: string): Node {
  return {
    id: `/workspace/${repo}`,
    name: repo,
    kind: "directory",
    path: `/workspace/${repo}`,
    children: [],
    loaded: false,
  };
}

export function FileTreePanel({ workspaceId, repo, onOpenFile }: Props) {
  const [root, setRoot] = useState<Node>(() => buildRoot(repo));
  const [error, setError] = useState("");

  useEffect(() => {
    setRoot(buildRoot(repo));
  }, [repo]);

  const loadChildren = useCallback(
    async (node: Node) => {
      if (node.loaded || node.kind !== "directory") return;
      try {
        const resp = await readWorkspaceFile(workspaceId, node.path);
        if (resp.kind !== "directory") return;
        const children: Node[] = resp.entries.map((e: SandboxDirEntry) => ({
          id: `${node.path}/${e.name}`,
          name: e.name,
          kind: e.kind,
          path: `${node.path}/${e.name}`,
          children: e.kind === "directory" ? [] : undefined,
          loaded: e.kind !== "directory",
        }));
        setRoot((prev) => replaceNode(prev, node.path, children));
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : `Could not list ${node.path}`,
        );
      }
    },
    [workspaceId],
  );

  // Seed the root's children on mount.
  useEffect(() => {
    void loadChildren(root);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo, workspaceId]);

  const data = useMemo(() => [root], [root]);

  return (
    <div
      className="flex h-full flex-col overflow-hidden"
      data-testid={`file-tree-${repo}`}
    >
      <Tree<Node>
        data={data}
        openByDefault={false}
        width="100%"
        height={300}
        indent={14}
        rowHeight={22}
        onActivate={(node: NodeApi<Node>) => {
          if (node.data.kind === "file") {
            onOpenFile(node.data.path);
          }
        }}
        onToggle={(id: string) => {
          const found = findNodeById(root, id);
          if (found) void loadChildren(found);
        }}
      >
        {({ node, style, dragHandle }) => {
          const data = node.data;
          const isDir = data.kind === "directory";
          const indent = style?.paddingLeft;
          return (
            <div
              ref={dragHandle}
              style={{ ...style, paddingLeft: indent }}
              className="flex cursor-pointer items-center gap-1 truncate px-1 text-[0.75rem] text-muted hover:text-primary"
              onClick={() => {
                if (isDir) node.toggle();
                else onOpenFile(data.path);
              }}
            >
              {isDir ? (
                node.isOpen ? (
                  <ChevronDown size={10} className="shrink-0 text-faint" />
                ) : (
                  <ChevronRight size={10} className="shrink-0 text-faint" />
                )
              ) : (
                <FileIcon size={10} className="shrink-0 text-faint" />
              )}
              <span className="truncate">{data.name}</span>
            </div>
          );
        }}
      </Tree>
      {error && (
        <p className="px-2 py-1 text-[0.625rem] text-danger">{error}</p>
      )}
    </div>
  );
}

function replaceNode(root: Node, path: string, children: Node[]): Node {
  if (root.path === path) {
    return { ...root, children, loaded: true };
  }
  if (!root.children) return root;
  return {
    ...root,
    children: root.children.map((c) => replaceNode(c, path, children)),
  };
}

function findNodeById(root: Node, id: string): Node | null {
  if (root.id === id) return root;
  if (!root.children) return null;
  for (const c of root.children) {
    const f = findNodeById(c, id);
    if (f) return f;
  }
  return null;
}
